import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Button, Space, message, Input, DatePicker, Spin, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Heading from "../../components/Heading";
import "./style.scss";
import { TableComponent } from "../Master/CommonComponents";
import search_icon from "../../assests/images/search-icon.svg";
import { getPremiseId } from "../../utils/worker";
import API from "../../api";
import urls from "../../api/urls";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import dayjs from "dayjs";

const ApprovalsCP = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvedAwbs, setApprovedAwbs] = useState(() => new Set());
  const [rejectedAwbs, setRejectedAwbs] = useState(() => new Set());

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Filters (declare before fetchData to avoid TDZ)
  const [awbQuery, setAwbQuery] = useState("");
  const [dateRange, setDateRange] = useState(() => [dayjs(), dayjs()]);
  const [cpNameQuery, setCpNameQuery] = useState("");

  // Helper to format values to Title Case (camel case words)
  const toTitleCase = (value) => {
    const str = (value ?? "").toString();
    if (!str) return "";
    return str
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b([a-z])/g, (m, p1) => p1.toUpperCase());
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const bearerPid = getPremiseId(); // e.g., "Bearer <id>"
      const hubId =
        typeof bearerPid === "string" && bearerPid.startsWith("Bearer")
          ? bearerPid.split(" ")[1]
          : bearerPid || 1;

      // Build query params for server-side filtering
      const params = new URLSearchParams();
      params.set("hubId", String(hubId));
      if (Array.isArray(dateRange)) {
        const [from, to] = dateRange;
        const fromIso = from ? from.startOf("day").toISOString() : null;
        const toIso = to ? to.endOf("day").toISOString() : null;
        if (fromIso) params.set("fromDate", fromIso);
        if (toIso) params.set("toDate", toIso);
      }
      const awbTrim = (awbQuery || "").trim();
      if (awbTrim) params.set("awbNumber", awbTrim);
      const cpTrim = (cpNameQuery || "").trim();
      if (cpTrim) params.set("cpName", cpTrim);
      params.set("page", String(pagination.current || 1));
      params.set("limit", String(pagination.pageSize || 10));

      const endpoint = `${urls.EXCEPTION_UPDATE_REQ_COURIER}?${params.toString()}`;
      const resp = await API.Api(endpoint).get();
      if (!resp?.status) throw new Error(resp?.response?.data?.message || "Request failed");

      const json = resp.response?.data;
      const list = Array.isArray(json?.data)
        ? json.data.map((it) => ({
          awbNumber: it?.awbNumber ?? "",
          status: it?.exceptionRequestStatus ?? "",
          cpId: String(it?.cpId ?? ""),
          cpName: it?.cpName ?? "",
          requestRaisedFor: it?.reqRaisedFor ?? "",
          date: it?.date ?? undefined,
          reason: it?.reason ?? "",
        }))
        : [];

      // Sort by date descending (latest first)
      list.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });

      setRows(list);
      setPagination((prev) => ({
        ...prev,
        total: json?.pagination?.totalCount ?? list.length,
      }));
    } catch (e) {
      console.error(e);
      message.error("Failed to load CP approvals data");
    } finally {
      setLoading(false);
    }
  }, [awbQuery, cpNameQuery, dateRange, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateExceptionStatus = async (awbNumber, nextStatus) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("userinfo")) || {};
      const approvedBy = user?.username || user?.email || "";
      const body = {
        awbNumber: String(awbNumber || ""),
        status: nextStatus, // "approved" | "rejected"
        source: "hubops",
        approvedBy,
      };

      const resp = await API.Api(urls.EXCEPTION_STATUS_COURIER).patchCustomHeader(body, {
        "Content-Type": "application/json",
      });
      if (!resp?.status) throw new Error(resp?.response?.data?.message || "Update failed");

      // Optimistically mark as approved/rejected for immediate UI feedback
      setApprovedAwbs((prev) => {
        const copy = new Set(prev);
        const key = String(awbNumber);
        if (nextStatus === "approved") {
          copy.add(key);
        } else {
          copy.delete(key);
        }
        return copy;
      });
      setRejectedAwbs((prev) => {
        const copy = new Set(prev);
        const key = String(awbNumber);
        if (nextStatus === "rejected") {
          copy.add(key);
        } else {
          copy.delete(key);
        }
        return copy;
      });

      message.config({ top: 50 });
      message.success(`Status ${nextStatus} for AWB ${awbNumber}`);
      await fetchData();
    } catch (e) {
      console.error(e);
      message.error(`Failed to ${nextStatus} for AWB ${awbNumber}`);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "index",
        align: "center",
        width: 70,
        render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      },
      {
        title: "Awb Number",
        dataIndex: "awbNumber",
        key: "awbNumber",
        sorter: true,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        sorter: true,
        render: (text) => {
          const val = String(text || "").toLowerCase();
          const color = val === "approved" ? "#52c41a" : val === "rejected" ? "#ff4d4f" : undefined;
          const weight = val === "approved" || val === "rejected" ? 600 : 400;
          const display = toTitleCase(text || "");
          return <span style={{ color, fontWeight: weight }}>{display || "-"}</span>;
        },
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        sorter: true,
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "CP Name",
        dataIndex: "cpName",
        key: "cpName",
        sorter: true,
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "Request Raised For",
        dataIndex: "requestRaisedFor",
        key: "requestRaisedFor",
        sorter: true,
        render: (text) => <span>{toTitleCase(text || "") || "-"}</span>,
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        sorter: true,
        render: (text) => <span>{text ? dayjs(text).format("DD-MM-YYYY | HH:mm:ss") : "-"}</span>,
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => {
          const isApproved =
            String(record?.status || "").toLowerCase() === "approved" ||
            approvedAwbs.has(String(record?.awbNumber));
          const isRejected =
            String(record?.status || "").toLowerCase() === "rejected" ||
            rejectedAwbs.has(String(record?.awbNumber));

          const isDisabledApprove = loading || isApproved;
          const isDisabledReject = loading || isRejected || isApproved;

          const approveStyle = {
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
            color: "#fff",
            opacity: isDisabledApprove ? 0.5 : 1,
            cursor: isDisabledApprove ? "not-allowed" : "pointer",
          };
          const rejectStyle = {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "#fff",
            opacity: isDisabledReject ? 0.5 : 1,
            cursor: isDisabledReject ? "not-allowed" : "pointer",
          };
          return (
            <Space>
              {!isRejected && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>Are you sure you want to approve this?</span>}
                  onConfirm={() => updateExceptionStatus(record?.awbNumber, "approved")}
                  okText="Yes"
                  cancelText="No"
                  disabled={isDisabledApprove}
                  icon={<QuestionCircleOutlined style={{ color: "green" }} />}
                >
                  <Button
                    shape="round"
                    size="small"
                    style={approveStyle}
                    disabled={isDisabledApprove}
                  >
                    Approve
                  </Button>
                </Popconfirm>
              )}
              {!isApproved && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>Are you sure you want to reject this?</span>}
                  onConfirm={() => updateExceptionStatus(record?.awbNumber, "rejected")}
                  okText="Yes"
                  cancelText="No"
                  disabled={isDisabledReject}
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                >
                  <Button
                    shape="round"
                    size="small"
                    style={rejectStyle}
                    disabled={isDisabledReject}
                  >
                    Reject
                  </Button>
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [pagination, loading, approvedAwbs, rejectedAwbs]
  );

  const filteredData = useMemo(() => rows, [rows]);

  // Reset to page 1 when filters change; do not override server total
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [awbQuery, cpNameQuery, dateRange]);

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const clearFilters = () => {
    setAwbQuery("");
    setCpNameQuery("");
    setDateRange(null);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <div className="approvals-cp-page" style={{ padding: "16px" }}>
      <Heading title={"CP Approvals"} navigateTo={"/approvals-settings"} />
      <Spin
        spinning={loading}
        indicator={<img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "nowrap", marginBottom: 12, overflowX: "auto" }}>
          <Input
            placeholder="Search AWB number"
            value={awbQuery}
            onChange={(e) => setAwbQuery(e.target.value)}
            allowClear
            suffix={<img src={search_icon} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <Input
            placeholder="Search CP Name"
            value={cpNameQuery}
            onChange={(e) => setCpNameQuery(e.target.value)}
            allowClear
            suffix={<img src={search_icon} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(vals) => setDateRange(vals)}
            allowEmpty={[true, true]}
            placeholder={["From date", "To date"]}
            style={{ width: 320, height: 40, flex: "0 0 auto" }}
          />
          <Button onClick={clearFilters} style={{ whiteSpace: "nowrap", height: 40 }} disabled={loading}>
            Clear
          </Button>
        </div>

        <TableComponent
          columns={columns}
          dataSource={filteredData}
          rowKey="awbNumber"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
        />
      </Spin>
    </div>
  );
};

export default ApprovalsCP;



